import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const res = await axios.get(`${API}/payments/status/${sessionId}`);
      setPaymentInfo(res.data);
      
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        refreshUser();
        return;
      } else if (res.data.status === 'expired') {
        setStatus('expired');
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking status:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md bg-zinc-900/50 border-zinc-800">
        <CardContent className="py-12 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="mx-auto text-red-500 animate-spin mb-6" size={48} />
              <h2 className="text-2xl font-bold text-white mb-4">Vérification du paiement...</h2>
              <p className="text-zinc-400">Veuillez patienter</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="text-green-500" size={40} />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Paiement Réussi !</h2>
              <p className="text-zinc-400 mb-2">Merci pour votre achat</p>
              {paymentInfo && (
                <p className="text-red-500 font-bold text-xl mb-8">
                  {(paymentInfo.amount_total / 100).toFixed(2)} €
                </p>
              )}
              <p className="text-zinc-500 mb-8">
                Des points de fidélité ont été ajoutés à votre compte !
              </p>
              <div className="flex justify-center gap-4">
                <Button onClick={() => navigate('/mon-compte')} className="bg-red-600 hover:bg-red-700">
                  Mon Compte
                </Button>
                <Button variant="outline" className="border-zinc-700" onClick={() => navigate('/')}>
                  Accueil
                </Button>
              </div>
            </>
          )}

          {(status === 'error' || status === 'timeout' || status === 'expired') && (
            <>
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-red-500 text-4xl">!</span>
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">
                {status === 'timeout' ? 'Délai dépassé' : status === 'expired' ? 'Session expirée' : 'Erreur'}
              </h2>
              <p className="text-zinc-400 mb-8">
                {status === 'timeout' 
                  ? 'La vérification du paiement a pris trop de temps. Vérifiez votre email.'
                  : 'Une erreur est survenue. Contactez-nous si vous avez été débité.'
                }
              </p>
              <Button onClick={() => navigate('/contact')} className="bg-red-600 hover:bg-red-700">
                Nous contacter
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
